import React, { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';

import AddStudentModal from '../dashboard/components/AddStudentModal';
import StudentRoster from './components/StudentRoster';
import StudentProfileView from './components/StudentProfileView';

// ---------------------------------------------------------------------------
// UnreachableDatabaseBanner
//
// Shown when a live-mode roster load failed with a "database unreachable"
// class of error — overwhelmingly the Supabase free-tier auto-pause after
// 7 days of inactivity, occasionally a transient network failure. The
// teacher-facing goals of this banner, in order:
//
//   1. Reassure. First line MUST say "your students are safe" in plain
//      English. That's the actual truth (data lives in Postgres, not in
//      the app) and it's the thing a panicking teacher needs to hear
//      first, before any instructions.
//   2. Explain, in one sentence a non-technical reader parses in <5s.
//      No mention of "Postgres", "REST", "5xx", "RLS", etc.
//   3. Give ONE primary action ("Wake it up") that opens the Supabase
//      dashboard, plus a secondary "Try again" for the transient-network
//      case where the fix is just retrying.
//   4. Show the last-known roster underneath rather than an empty screen,
//      so the teacher can visually confirm their students are still
//      there while they wake the DB.
// ---------------------------------------------------------------------------

const SUPABASE_DASHBOARD_URL = 'https://supabase.com/dashboard';

function formatRelativeTime(ms) {
  if (!ms || typeof ms !== 'number') return null;
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const mins = Math.round(diff / 60_000);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function UnreachableDatabaseBanner({
  usingCachedSnapshot,
  snapshotSavedAt,
  cachedCount,
  onRetry,
}) {
  const [retrying, setRetrying] = useState(false);
  const lastSynced = formatRelativeTime(snapshotSavedAt);

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await onRetry?.();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      role="status"
      className="mb-4 p-4 rounded-md bg-amber-50 border border-amber-200"
    >
      <div className="flex items-start gap-3">
        <Icon
          name="Moon"
          size={20}
          className="text-amber-700 flex-shrink-0 mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900">
            Your database is asleep — your students are safe.
          </p>
          <p className="text-sm text-amber-900 mt-1">
            Supabase (the free service that stores your class list) pauses
            projects after about a week of no activity. Nothing is lost.
            Wake it up from the Supabase dashboard and your roster will be
            back in a minute.
          </p>
          {usingCachedSnapshot && cachedCount > 0 && (
            <p className="text-xs text-amber-800 mt-2">
              Showing your last-known roster
              {lastSynced ? ` (from ${lastSynced})` : ''}
              {' — '}
              <span className="font-medium">
                {cachedCount} student{cachedCount === 1 ? '' : 's'}
              </span>
              . New changes won't save until the database is back.
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <a
              href={SUPABASE_DASHBOARD_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium"
            >
              <Icon name="ExternalLink" size={14} />
              Wake it up on Supabase
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              disabled={retrying}
              iconName={retrying ? 'Loader2' : 'RefreshCw'}
              iconPosition="left"
              iconSize={14}
              className={retrying ? '[&_svg]:animate-spin' : ''}
            >
              {retrying ? 'Checking…' : 'Try again'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Lighter-weight version of the same idea for when the roster on
// screen came from cache but we haven't decided we're in "unreachable"
// mode yet — e.g. the initial paint before the first fetch resolves.
function CachedSnapshotIndicator({ snapshotSavedAt, onRefresh }) {
  const lastSynced = formatRelativeTime(snapshotSavedAt);
  return (
    <div className="mb-3 flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-muted/50 border border-border text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <Icon name="Clock" size={12} />
        Showing your last-known roster
        {lastSynced ? ` (synced ${lastSynced})` : ''}. Refreshing…
      </span>
      <button
        type="button"
        onClick={onRefresh}
        className="text-xs font-medium text-foreground hover:underline"
      >
        Refresh now
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Student profile management — the sidebar's "Student Profiles" landing page.
//
// One page, two modes:
//
//   * /student-profile-management              -> class roster (list view)
//   * /student-profile-management/:studentId   -> single student profile
//
// Legacy nav pattern: some older callers still pass studentId via
// location.state (from before we had URL params). We accept both — if a
// state.studentId is present but the URL has none, we redirect to the
// clean URL so refreshes and shared links keep working.
//
// This file replaces the previous 600-line hardcoded "Alex Thompson" mock.
// ---------------------------------------------------------------------------

const StudentProfileManagement = () => {
  const { studentId: urlId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);

  const {
    students,
    loading,
    error,
    isLive,
    unreachable,
    usingCachedSnapshot,
    snapshotSavedAt,
    refresh,
  } = useStudents();

  // Legacy state-based nav: normalize onto the URL param form.
  useEffect(() => {
    const stateId = location.state?.studentId;
    if (!urlId && stateId) {
      navigate(`/student-profile-management/${encodeURIComponent(stateId)}`, {
        replace: true,
        state: null,
      });
    }
  }, [urlId, location.state, navigate]);

  const decodedId = urlId ? decodeURIComponent(urlId) : null;

  const selected = useMemo(
    () => (decodedId ? students.find((s) => s.id === decodedId) : null),
    [students, decodedId],
  );

  const mode = decodedId ? 'profile' : 'roster';
  const stillLoading = mode === 'profile' && loading && !selected;

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="pt-16 min-h-screen">
          {mode === 'roster' && (
            <div className="max-w-7xl mx-auto p-4 sm:p-6">
              {/* Database-unreachable banner. Wins over `error` because
                  we want the teacher-friendly "your DB is napping, click
                  here" wording, not the scary generic "Couldn't load
                  students" text. Shown regardless of whether the cached
                  snapshot happens to be empty. */}
              {unreachable && isLive && (
                <UnreachableDatabaseBanner
                  usingCachedSnapshot={usingCachedSnapshot}
                  snapshotSavedAt={snapshotSavedAt}
                  cachedCount={students.length}
                  onRetry={refresh}
                />
              )}

              {/* Fallback: any non-pause error the context bubbled up.
                  In practice this is only the schema / RLS / auth path,
                  because isDatabaseUnreachableError() takes over for the
                  pause and network cases upstream. */}
              {error && !unreachable && (
                <div className="mb-4 p-3 rounded-md bg-destructive/10 border border-destructive/30">
                  <div className="flex items-start gap-2">
                    <Icon
                      name="AlertCircle"
                      size={16}
                      className="text-destructive flex-shrink-0 mt-0.5"
                    />
                    <div className="text-sm text-foreground">
                      <p className="font-medium">
                        Couldn't load students from the database
                      </p>
                      <p className="text-muted-foreground mt-1">{error}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {isLive
                          ? 'This usually means the Supabase students table is missing or RLS is blocking reads. Ask your admin, or run supabase/migrations/0001_students.sql.'
                          : 'Demo mode: this shouldn’t happen locally.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cached-snapshot indicator when the DB is up but the
                  current render came from cache (e.g. optimistic first
                  paint before the fetch resolves, or a background
                  refresh after a transient failure). Only shown while
                  we're NOT already showing the bigger unreachable
                  banner — that one already conveys the same thing. */}
              {!unreachable && usingCachedSnapshot && (
                <CachedSnapshotIndicator
                  snapshotSavedAt={snapshotSavedAt}
                  onRefresh={refresh}
                />
              )}

              <StudentRoster
                onAddStudent={() => setIsAddStudentOpen(true)}
              />
            </div>
          )}

          {mode === 'profile' && stillLoading && (
            <div className="min-h-[60vh] flex items-center justify-center">
              <div className="text-center">
                <Icon
                  name="Loader2"
                  size={40}
                  className="text-primary mx-auto mb-3 animate-spin"
                />
                <p className="text-base font-medium text-foreground">
                  Loading student profile…
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Fetching data from your class roster
                </p>
              </div>
            </div>
          )}

          {mode === 'profile' && !stillLoading && !selected && (
            <div className="max-w-2xl mx-auto p-6">
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <Icon
                  name="UserX"
                  size={40}
                  className="text-muted-foreground mx-auto mb-3"
                />
                <h2 className="text-lg font-semibold text-foreground">
                  Student not found
                </h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                  We couldn't find a student with ID{' '}
                  <span className="font-mono">{decodedId}</span> in your roster.
                  They may have been removed, or the link may be for a different
                  account.
                </p>
                <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                  <Button
                    variant="default"
                    onClick={() => navigate('/student-profile-management')}
                    iconName="Users"
                    iconPosition="left"
                  >
                    Back to roster
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    iconName="LayoutDashboard"
                    iconPosition="left"
                  >
                    Go to dashboard
                  </Button>
                </div>
              </div>
            </div>
          )}

          {mode === 'profile' && selected && (
            <StudentProfileView student={selected} />
          )}
        </div>
      </main>

      <AddStudentModal
        isOpen={isAddStudentOpen}
        onClose={() => setIsAddStudentOpen(false)}
        onCreated={(student) => {
          if (student?.id) {
            navigate(`/student-profile-management/${encodeURIComponent(student.id)}`);
          }
        }}
      />
    </div>
  );
};

export default StudentProfileManagement;
